import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core'
import {
  CmsComponentData,
  SearchBoxComponent,
  SearchBoxComponentService,
  SearchBoxConfig,
  SearchResults
} from '@spartacus/storefront'
import {
  CmsSearchBoxComponent,
  ProductSearchService,
  RoutingService,
  TranslationService,
  WindowRef
} from '@spartacus/core'
import { Observable, of, Subscription } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { UrlUtils } from 'src/app/mb/shared/utils/url-utils'
import { SearchUtilsService } from '../../../search/services/search-utils.service'
import { Router } from '@angular/router'
import { MbConstants, searchTermCharLimit } from '../../mb-constants'
import { MbEventListenerService } from '../../../cms/services/mb-event-listener/event-listener.service'
import { SearchCloseEvent } from '../../../cms/components/types'
import { MbSpinnerService } from '../../services/mb-spinner.service'

const MB_SEARCH_BOX_CONFIG: SearchBoxConfig = {
  minCharactersBeforeRequest: 2,
  displayProducts: true,
  displaySuggestions: true,
  maxProducts: 5,
  maxSuggestions: 5,
  displayProductImages: true
}

@Component({
  selector: 'mb-search-box-component',
  templateUrl: './mb-search-box.component.html',
  styleUrls: ['./mb-search-box.component.scss']
})
export class MbSearchBoxComponent extends SearchBoxComponent implements OnInit, OnDestroy {

  isCategoryPageFlag: boolean = false

  @Input()
  set isCategoryPage (isCategoryPage: boolean) {
    this.isCategoryPageFlag = isCategoryPage
    this.searchFilterService.shrinkLinesForCategoryPage(isCategoryPage)
    this.subscription.add(this.config$.subscribe())
  }

  get isCategoryPage (): boolean {
    return this.isCategoryPageFlag
  }

  @Input()
  searchQuery: string | undefined

  @Output()
  searchTerm: EventEmitter<string> = new EventEmitter<string>()

  @Input()
  isInitialFocused: boolean = false

  subscription: Subscription = new Subscription()
  constants = MbConstants
  searchTermCharLimit = searchTermCharLimit
  isFocused: boolean = false

  constructor (searchBoxComponentService: SearchBoxComponentService,
               componentData: CmsComponentData<CmsSearchBoxComponent>,
               winRef: WindowRef,
               routingService: RoutingService,
               private productSearchService: ProductSearchService,
               private searchFilterService: SearchUtilsService,
               private translation: TranslationService,
               protected eventListenerService: MbEventListenerService,
               private router: Router,
               protected mbSpinnerService: MbSpinnerService,
               protected vcr: ViewContainerRef) {
    super(searchBoxComponentService, componentData, winRef, routingService)
  }

  protected config$: Observable<SearchBoxConfig> = (this.componentData?.data$ || of({} as any)).pipe(
    map((config) => {
      const isBool = (obj: SearchBoxConfig, prop: string): boolean =>
        (obj as any)?.[prop] !== 'false' && (obj as any)?.[prop] !== false

      return {
        ...MB_SEARCH_BOX_CONFIG,
        ...config,
        minCharactersBeforeRequest: MB_SEARCH_BOX_CONFIG?.minCharactersBeforeRequest,
        displayProducts: isBool(config, 'displayProducts'),
        displayProductImages: isBool(config, 'displayProductImages'),
        displaySuggestions: isBool(config, 'displaySuggestions'),
        ...this.config
      }
    }),
    tap((config) => (this.config = config))
  )

  results$: Observable<SearchResults> = this.config$.pipe(
    switchMap((config) => this.searchBoxComponentService.getResults(config))
  )

  ngOnInit (): void {
    this.searchFilterService.shrinkLinesForCategoryPage(this.isCategoryPageFlag)

    this.subscription.add(this.routingService.getRouterState()
      .subscribe(state => {
        const query: string = state.state.params['query']
        if (query) {
          super.updateChosenWord(query)
        }
      }))
  }

  ngOnDestroy () {
    this.subscription?.unsubscribe()
  }

  launchSearchEvents (event: UIEvent, value: string): void {
    if (!this.isCategoryPage) {
      if (value?.toLowerCase() !== this.chosenWord?.toLowerCase() && value?.trim() !== '') {
        this.mbSpinnerService.launchMbSpinner(this.vcr)
      }
      if (this.searchQuery?.length) {
        this.emitNewSearchTermFromEnterOrClickEvents(event, value)
        this.launchCustomSearchResult(event, this.searchQuery, value)
      } else {
        this.launchSearchResult(event, value)
      }
    } else {
      this.emitNewSearchTermFromEnterOrClickEvents(event, value)
    }
    super.updateChosenWord(value)
  }

  search (query: string): void {
    if (!this.isCategoryPage) {
      this.searchBoxComponentService.search(query, this.config)
      // force the searchBox to open
      this.open()
      this.searchTerm.emit(query)
    }
  }

  close (event: UIEvent, force?: boolean) {
    super.close(event, force)
    if (event?.type !== 'blur') {
      this.eventListenerService.update({
        type: 'searchClose'
      } as SearchCloseEvent)
    }
  }

  open (): void {
    if (!this.isCategoryPage) {
      this.searchBoxComponentService.toggleBodyClass('searchbox-is-active', true)
    }
  }

  launchCustomSearchResult (event: UIEvent, query: string, searchTerm?: string): void {
    if (!query || query.trim().length === 0) {
      return
    }

    this.close(event)
    this.router.navigate(['search/' + searchTerm], {
      queryParams: {
        query: query
      }
    }).catch((error: Error) => console.error(error))
  }

  launchSearchResult (event: UIEvent, query: string): void {
    if (!query || query.trim().length === 0) {
      return
    }

    this.close(event)
    this.router.navigate(['search/' + query]).catch((error: Error) => console.error(error))
  }

  getRouterLink (url: string): string {
    return UrlUtils.getPath(url)?.toLowerCase()
  }

  getFragment (url: string): string {
    return UrlUtils.getFragment(url)?.toLowerCase()
  }

  getPriceLabel (product: any): Observable<string> {
    return product?.variantOptions?.length > 1 && !product?.priceOnRequest ? this.translation.translate('searchResultItem.priceFrom') :
      product?.variantOptions?.length === 1 && !product?.variantOptions?.[0]?.priceOnRequest ? this.translation.translate('searchResultItem.price'):
      this.translation.translate('mbcommon.priceOnRequest')
  }

  getProductName (product: any): string {
    return product?.variantOptions?.length > 1 ? product?.name : product?.variantOptions?.length === 1 ? product?.variantOptions[0]?.name : ''
  }

  navigateToSearchPage (suggestion: string) {
    if (this.searchQuery?.length) {
      this.router.navigate(['search/' + suggestion], {
        queryParams: {
          query: suggestion + this.searchQuery.slice(this.searchQuery.indexOf(this.constants.COLON_SYMBOL))
        },
        queryParamsHandling: 'merge'
      }).catch((error: Error) => console.error(error))
    } else {
      this.routingService.go({
        cxRoute: 'search',
        params: { query: suggestion }
      }).catch((error: Error) => console.error(error))
    }
  }

  toggleInputFocus (isFocused: boolean): void {
    if (!this.isCategoryPage) {
      this.isFocused = isFocused
    }
  }

  focusNextChild (event: UIEvent): void {
    this.open()
    event.preventDefault() // Negate normal keyscroll
    const [results, focusedIndex] = [
      this.getSuggestionProductResult(),
      this.getActiveElementIndex()
    ]
    // Focus on first index moving to last
    if (results.length) {
      if (focusedIndex >= results.length - 1) {
        (results[0] as HTMLElement).focus()
      } else {
        (results[focusedIndex + 1] as HTMLElement).focus()
      }
    }
  }

  focusPreviousChild (event: UIEvent): void {
    this.open()
    event.preventDefault() // Negate normal keyscroll
    const [results, focusedIndex] = [
      this.getSuggestionProductResult(),
      this.getActiveElementIndex()
    ]
    if (results.length) {
      if (focusedIndex < 1) {
        (results[results.length - 1] as HTMLElement).focus()
      } else {
        (results[focusedIndex - 1] as HTMLElement).focus()
      }
    }
  }

  getSuggestionProductResult (): HTMLElement[] {
    return Array.from(this.winRef?.document?.querySelectorAll('.is-focused .products > li a, .is-focused .suggestions > li a'))
  }

  getActiveElementIndex (): number {
    return this.getSuggestionProductResult().indexOf(this.winRef?.document?.activeElement as HTMLElement)
  }

  private emitNewSearchTermFromEnterOrClickEvents (event: UIEvent, value: string): void {
    if ((event as any)?.keyCode === 13 || (event?.target as any)?.id === 'searchIconIDUsedForSearching') {
      this.searchTerm.emit(value)
    }
  }

  clear (el: HTMLInputElement) {
    el.value = ''
    this.searchBoxComponentService.clearResults()
  }

  reset (el: HTMLInputElement, event: UIEvent) {
    this.close(event)
    el.value = ''
    this.searchTerm.emit('')
  }
}
